import { useCallback, useState, useRef, ReactNode } from 'react';

interface ZoomBrushConfig {
  minSelectionWidthPixels: number;
  minZoomDurationMinutes: number;
  marginLeft: number;
  marginRight: number;
}

interface ZoomBrushProps {
  children: ReactNode;
  disabled: boolean;
  timeRange: { start: Date; end: Date } | null;
  zoomDomain: { xStart: Date; xEnd: Date } | null;
  onZoomChange?: (zoomDomain: { xStart: Date; xEnd: Date } | null) => void;
  className?: string;
  config?: Partial<ZoomBrushConfig>;
}

const DEFAULT_CONFIG: ZoomBrushConfig = {
  minSelectionWidthPixels: 20,
  minZoomDurationMinutes: 60,
  marginLeft: 70,
  marginRight: 40,
};

interface Selection {
  start: number;
  end: number;
  isActive: boolean;
}

export const ZoomBrush = ({
  children,
  disabled,
  timeRange,
  zoomDomain,
  onZoomChange,
  className = '',
  config: userConfig = {},
}: ZoomBrushProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<Selection | null>(null);

  const config = { ...DEFAULT_CONFIG, ...userConfig };

  const getRelativePosition = useCallback(
    (clientX: number): number | null => {
      if (!containerRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const chartAreaLeft = config.marginLeft;
      const chartAreaRight = rect.width - config.marginRight;

      if (x < chartAreaLeft || x > chartAreaRight) return null;

      const chartAreaWidth = chartAreaRight - chartAreaLeft;
      const relativeX = (x - chartAreaLeft) / chartAreaWidth;

      return Math.max(0, Math.min(1, relativeX));
    },
    [config.marginLeft, config.marginRight],
  );

  const convertToTime = useCallback(
    (relativePos: number): Date => {
      const currentStart = zoomDomain?.xStart || timeRange?.start;
      const currentEnd = zoomDomain?.xEnd || timeRange?.end;

      if (!currentStart || !currentEnd) return new Date();

      const duration = currentEnd.getTime() - currentStart.getTime();
      return new Date(currentStart.getTime() + duration * relativePos);
    },
    [zoomDomain, timeRange],
  );

  const validateAndCreateZoom = useCallback(
    (start: number, end: number) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const chartAreaWidth = rect.width - config.marginLeft - config.marginRight;
      const selectionWidth = Math.abs(end - start) * chartAreaWidth;

      if (selectionWidth < config.minSelectionWidthPixels) return;

      const startPos = Math.min(start, end);
      const endPos = Math.max(start, end);
      const startTime = convertToTime(startPos);
      const endTime = convertToTime(endPos);

      const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);

      if (durationMinutes >= config.minZoomDurationMinutes) {
        onZoomChange?.({ xStart: startTime, xEnd: endTime });
      }
    },
    [config, convertToTime, onZoomChange],
  );

  const getSelectionStyle = useCallback(() => {
    if (!selection?.isActive || !containerRef.current) {
      return { display: 'none' as const };
    }

    const rect = containerRef.current.getBoundingClientRect();
    const chartAreaLeft = config.marginLeft;
    const chartAreaWidth = rect.width - config.marginLeft - config.marginRight;

    const leftPos = Math.min(selection.start, selection.end);
    const rightPos = Math.max(selection.start, selection.end);

    const left = chartAreaLeft + leftPos * chartAreaWidth;
    const width = (rightPos - leftPos) * chartAreaWidth;

    return {
      left: `${left}px`,
      width: `${width}px`,
      height: '100%',
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      border: '1px solid rgb(59, 130, 246)',
    };
  }, [selection, config.marginLeft, config.marginRight]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (disabled || event.button !== 0) return;

      const pos = getRelativePosition(event.clientX);
      if (pos === null) return;

      setSelection({ start: pos, end: pos, isActive: true });
      event.preventDefault();
    },
    [disabled, getRelativePosition],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!selection?.isActive) return;

      const pos = getRelativePosition(event.clientX);
      if (pos === null) return;

      setSelection(prev => (prev ? { ...prev, end: pos } : null));
      event.preventDefault();
    },
    [selection?.isActive, getRelativePosition],
  );

  const handleMouseUp = useCallback(
    (event: React.MouseEvent) => {
      if (!selection?.isActive) return;

      validateAndCreateZoom(selection.start, selection.end);
      setSelection(null);
      event.preventDefault();
    },
    [selection, validateAndCreateZoom],
  );

  const handleMouseLeave = useCallback(() => {
    setSelection(null);
  }, []);

  if (disabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={`relative select-none ${className}`}
      style={{ cursor: 'crosshair' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {selection?.isActive && (
        <div
          className="pointer-events-none absolute top-0"
          style={{
            ...getSelectionStyle(),
            zIndex: 1000,
          }}
        />
      )}
      <div className="h-full w-full">{children}</div>
    </div>
  );
};
